import React, { Suspense, useEffect, useLayoutEffect, useRef } from 'react';

import { Navigate, Route, useLocation, useNavigate } from 'react-router-dom';
import { PageNotFound, Text } from '@pflegenavi/web-components';
import {
  DevLangSwitcher,
  localeStorage,
} from '@pflegenavi/frontend/localization-web-app';
import { useTranslation } from 'react-i18next';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { DashboardLayout } from '@pflegenavi/frontend/layout';
import { DashboardPage } from '../pages/DashboardPage';
import { useAuthentication } from '@pflegenavi/frontend/authentication';
import { useSyncLocaleWithServer } from '@pflegenavi/shared/frontend';
import { Box, CircularProgress, styled } from '@mui/material';
import { experimental_sx as sx } from '@mui/system';
import { ConnectAccountPage } from '../pages/ConnectAccountPage';
import { NewInvitationPage } from '../pages/NewInvitationPage';
import { AddReceiptBatchPage } from '../pages/AddReceiptBatchPage';
import EditReceiptBatchPage from '../pages/EditReceiptBatchPage';
import { renderSwitchLanguageMenuItem } from '../components/general/SwitchLanguageMenuItem';
import { ProfilePage } from '../pages/ProfilePage';
import { ServiceProviderProfileDetails } from '../components/profile/ServiceProviderProfileDetails';
import { SentryRoutes } from '../sentry';
import ServiceProviderStripeOnboardingRefresh from '../components/profile/ServiceProviderStripeOnboardingRefresh';
import ServiceProviderStripeOnboardingComplete from '../components/profile/ServiceProviderStripeOnboardingComplete';
import { ServicesOverviewPage } from '../pages/ServicesOverviewPage';
import {
  Login,
  RedirectResetPassword,
} from '@pflegenavi/shared-web/authentication';
import { shouldUseNewLogin } from '@pflegenavi/shared-frontend/authentication';
import { useFeatureFlagModal } from '@pflegenavi/frontend/feature-flags-web';
import { useGetNursingHomeList } from '@pflegenavi/service-provider-frontend/api';
import { ConnectionStatus } from '@pflegenavi/service-provider-shared/api-endpoints';

// eslint-disable-next-line complexity
export default function App(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthentication();
  const location = useLocation();

  useSyncLocaleWithServer({ localeStorage });

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon || !user) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('identify', {
      name: `${user.firstName} ${user.lastName}`,
      email: user?.email,
      tenant: 'service_provider',
      'user-id': user.userId,
      environment: window.location.hostname.startsWith('localhost')
        ? 'local'
        : window.location.hostname.includes('.staging.')
        ? 'staging'
        : 'production',
      application: 'Service Provider web',
    });
  }, [user]);

  useEffect(() => {
    // @ts-expect-error Beacon is untyped
    if (!window.Beacon) {
      return;
    }

    // @ts-expect-error Beacon is untyped
    window.Beacon('event', {
      type: 'page-viewed',
      url: location.pathname,
      title: document.title,
    });
    // @ts-expect-error Beacon is untyped
    window.Beacon('suggest');
    if (location && location.pathname === '/') {
      // @ts-expect-error Beacon is untyped
      window.Beacon('navigate', '/');
    }
  }, [location.pathname, location]);

  const { FeatureFlagModalComponent } = useFeatureFlagModal();

  const { isLoading } = useRedirectToConnectAccountPage(Boolean(user));

  if (!user) {
    if (shouldUseNewLogin()) {
      return (
        <>
          <Login />
        </>
      );
    }
  }

  if (!user || isLoading) {
    return (
      <LoadingContainer>
        <CircularProgress variant="indeterminate" />
      </LoadingContainer>
    );
  }

  return (
    <Suspense
      fallback={
        <Text color="primary" variant="h2">
          {t('')}
        </Text>
      }
    >
      {FeatureFlagModalComponent}
      {!import.meta.env.PROD && <DevLangSwitcher />}
      <SentryRoutes>
        <Route
          path={'/reset-password/:token'}
          element={<RedirectResetPassword />}
        />
        <Route
          path={spAppMainPages.CONNECT_ACCOUNT}
          element={<ConnectAccountPage />}
        />
        <Route
          path={spAppMainPages.NEW_INVITATION}
          element={<NewInvitationPage />}
        />
        <Route
          path={spAppMainPages.HOME}
          element={
            <DashboardLayout
              configKey="sp-web"
              helpUrl={
                'https://pn.helpscoutdocs.com/category/140-service-provider-webapp'
              }
              home={spAppMainPages.HOME}
              renderSwitchLanguageMenuItem={renderSwitchLanguageMenuItem}
            />
          }
        >
          <Route path={spAppMainPages.DASHBOARD} element={<DashboardPage />} />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_NEW}
            element={<AddReceiptBatchPage />}
          />
          <Route
            path={spAppMainPages.RECEIPT_BATCH_EDIT}
            element={<EditReceiptBatchPage />}
          />
          <Route
            path={spAppMainPages.SERVICES}
            element={<ServicesOverviewPage />}
          />
          <Route
            path={spAppMainPages.HOME}
            element={<Navigate to={spAppMainPages.DASHBOARD} replace />}
          />
          <Route path={spAppMainPages.WELCOME} element={<LoadingContainer />} />
          <Route path={spAppMainPages.PROFILE} element={<ProfilePage />}>
            <Route index={true} element={<ServiceProviderProfileDetails />} />
          </Route>
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_COMPLETE}
            element={<ServiceProviderStripeOnboardingComplete />}
          />
          <Route
            path={spAppMainPages.STRIPE_ONBOARDING_REFRESH}
            element={<ServiceProviderStripeOnboardingRefresh />}
          />
          <Route
            path="*"
            element={
              <PageNotFound
                handleGoHomeClick={() => navigate(spAppMainPages.HOME)}
              />
            }
          />
        </Route>
      </SentryRoutes>
    </Suspense>
  );
}

const useRedirectToConnectAccountPage = (isLoggedIn: boolean) => {
  const navigate = useNavigate();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const { data: nursingHomeList, isLoading: isNursingHomeListLoading } =
    useGetNursingHomeList({
      enabled: isLoggedIn,
    });

  const hasActiveNursingHome = Boolean(
    nursingHomeList?.find(
      (nursingHome) => nursingHome.connectionStatus === ConnectionStatus.Active
    )
  );
  const pendingNursingHome = nursingHomeList?.find(
    (nursingHome) =>
      nursingHome.connectionStatus === ConnectionStatus.InvitePending
  );

  const hasPendingNursingHome = Boolean(pendingNursingHome);

  useLayoutEffect(() => {
    if (isLoggedIn && hasPendingNursingHome) {
      if (!hasActiveNursingHome) {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.CONNECT_ACCOUNT}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      } else {
        navigateRef.current(
          {
            pathname: `${spAppMainPages.NEW_INVITATION}?tenant=${
              pendingNursingHome!.tenant
            }&nursingHomeId=${
              pendingNursingHome!.nursingHomeId
            }&globalServiceProviderId=${
              pendingNursingHome!.globalServiceProviderId
            }`,
          },
          { replace: true }
        );
      }
    }
    // eslint-disable-next-line @grncdr/react-hooks/exhaustive-deps
  }, [
    hasActiveNursingHome,
    isLoggedIn,
    navigateRef,
    hasPendingNursingHome,
    pendingNursingHome?.tenant,
    pendingNursingHome?.nursingHomeName,
    pendingNursingHome?.nursingHomeId,
    pendingNursingHome?.globalServiceProviderId,
  ]);

  return {
    isLoading: isNursingHomeListLoading || !isLoggedIn,
  };
};

const LoadingContainer = styled(Box)(
  sx({
    display: 'flex',
    width: '100%',
    height: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  })
);
